/**
 * segmentVisual — map a reconstructed redline segment (op × author) to its
 * visual encoding in the editing view. Pure so it can be unit-tested apart
 * from the React tree.
 *
 * Author distinguishes drafter (the v1→v2 diff / decision result) from
 * reviewer (a user edit op): drafter changes use the change-* business
 * palette (ADR-105 §5); reviewer edits use the brand palette so "what I
 * changed in this session" reads apart from "what the diff proposed".
 */
import type { RedlineAuthor, RedlineOp } from '../../lib/veridoc-api';

export interface SegmentVisual {
  /** Short state label, e.g. 未变更 / 新增 / 删除 / 移动 / 已编辑. */
  badge: string;
  /** Tailwind classes for the badge chip (bg + fg). */
  badgeClass: string;
  /** Left-bar + padding for the row container. */
  containerClass: string;
  /** Strike-through (a deletion preview). */
  struck: boolean;
  /** Faded text (discarded / deleted side). */
  muted: boolean;
}

export function segmentVisual(op: RedlineOp, author: RedlineAuthor): SegmentVisual {
  if (author === 'reviewer') {
    if (op === 'delete') {
      return {
        badge: '已删除',
        badgeClass: 'bg-change-deletion-bg text-change-deletion-fg',
        containerClass: 'border-l-2 border-change-deletion-bar pl-3',
        struck: true,
        muted: true,
      };
    }
    return {
      badge: op === 'insert' ? '已新增' : '已编辑',
      badgeClass: 'bg-brand-100 text-brand-700',
      containerClass: 'border-l-2 border-brand-500 pl-3',
      struck: false,
      muted: false,
    };
  }

  // drafter (diff / decision result)
  switch (op) {
    case 'insert':
      return {
        badge: '新增',
        badgeClass: 'bg-change-addition-bg text-change-addition-fg',
        containerClass: 'border-l-2 border-change-addition-bar pl-3',
        struck: false,
        muted: false,
      };
    case 'delete':
      return {
        badge: '删除',
        badgeClass: 'bg-change-deletion-bg text-change-deletion-fg',
        containerClass: 'border-l-2 border-change-deletion-bar pl-3',
        struck: true,
        muted: true,
      };
    case 'move':
      return {
        badge: '移动',
        badgeClass: 'bg-change-move-bg text-change-move-fg',
        containerClass: 'border-l-2 border-change-move-bar pl-3',
        struck: false,
        muted: false,
      };
    case 'keep':
    default:
      return {
        badge: '未变更',
        badgeClass: 'bg-ink-100 text-ink-500',
        containerClass: 'pl-3',
        struck: false,
        muted: false,
      };
  }
}
